import React, { useEffect, useRef, useState } from 'react';
import Modal from './Modal';
import './BookingModal.css';

export default function BookingModal({
  open,
  onClose,
  editingBookingId,
  error,
  handleSubmit,
  form,
  handleChange,
  aircraftList,
  trainingOptions,
  trainingOptionsLoading = false,
  usersMap,
  canCreateTrainingBooking,
  buildAircraftDisplayLabel,
  selectedAircraft,
  getAircraftServiceabilityMeta,
  setForm,
  parseDateTime,
  deriveDurationInput,
  baseAirfield,
  inputStyle,
  buttonStyle,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!open) {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (!error) return;

    submittingRef.current = false;
    setIsSubmitting(false);
  }, [error]);

  const errorText =
    typeof error === 'string'
      ? error
      : error?.message || '';

  const displayError =
    errorText &&
    (errorText.includes('bookings_aircraft_date_time_unique') ||
      errorText.toLowerCase().includes('duplicate key'))
      ? 'This booking has already been created.'
      : errorText;
  const isTraining = form.bookingType === 'training';
  const selectedStudentProfile = (trainingOptions?.studentProfiles || []).find(
    (profile) => String(profile.user_id) === String(form.studentUserId)
  );
  const availableEnrolments = selectedStudentProfile
    ? (trainingOptions?.enrolments || []).filter(
        (enrolment) => String(enrolment.student_profile_id) === String(selectedStudentProfile.id)
          && enrolment.status === 'active'
      )
    : [];

  function handleFormSubmit(event) {
    event.preventDefault();

    if (submittingRef.current) return;

    submittingRef.current = true;
    setIsSubmitting(true);

    try {
      handleSubmit(event);
    } catch (submitError) {
      submittingRef.current = false;
      setIsSubmitting(false);
      throw submitError;
    }
  }

  function handleEndDateChange(event) {
    const value = event.target.value;

    setForm((prev) => {
      const safeEndDate =
        value && value >= prev.date
          ? value
          : prev.date;

      const next = {
        ...prev,
        endDate: safeEndDate,
        showEndDate: safeEndDate !== prev.date,
      };

      const start = parseDateTime(
        next.date,
        next.time
      );

      const end = parseDateTime(
        next.endDate,
        next.endTime
      );

      return {
        ...next,
        durationInput: deriveDurationInput(
          start,
          end
        ),
      };
    });
  }

  function handleStartTimeChange(event) {
    const value = event.target.value;

    setForm((prev) => {
      const next = {
        ...prev,
        time: value,
        endDate: prev.endDate || prev.date,
      };

      const start = parseDateTime(
        next.date,
        next.time
      );

      const end = parseDateTime(
        next.endDate,
        next.endTime
      );

      return {
        ...next,
        durationInput: deriveDurationInput(
          start,
          end
        ),
      };
    });
  }

  function handleEndTimeChange(event) {
    const value = event.target.value;

    setForm((prev) => {
      const next = {
        ...prev,
        endTime: value,
        endDate: prev.endDate || prev.date,
        showEndDate:
          (prev.endDate || prev.date) !==
          prev.date,
      };

      const start = parseDateTime(
        next.date,
        next.time
      );

      const end = parseDateTime(
        next.endDate,
        next.endTime
      );

      return {
        ...next,
        durationInput: deriveDurationInput(
          start,
          end
        ),
      };
    });
  }

  function handleReturnDateChoice(event) {
    const sameDate =
      event.target.value === 'yes';

    setForm((prev) => {
      const next = {
        ...prev,
        showEndDate: !sameDate,
        endDate: sameDate
          ? prev.date
          : prev.endDate && prev.endDate > prev.date
            ? prev.endDate
            : getNextCalendarDate(prev.date),
      };

      const start = parseDateTime(
        next.date,
        next.time
      );

      const end = parseDateTime(
        next.endDate,
        next.endTime
      );

      return {
        ...next,
        durationInput: deriveDurationInput(
          start,
          end
        ),
      };
    });
  }

  function getNextCalendarDate(dateValue) {
    const date = new Date(`${dateValue}T12:00:00`);
    if (!Number.isFinite(date.getTime())) return dateValue;

    date.setDate(date.getDate() + 1);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  function formatDepartureDate(dateValue) {
    const date = new Date(`${dateValue}T12:00:00`);
    if (!Number.isFinite(date.getTime())) return dateValue || 'Not set';

    return date.toLocaleDateString(undefined, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  return (
    <Modal open={open} onClose={onClose} variant="booking">
      <div style={styles.kicker}>
        BOOKING
      </div>

      <h3 style={styles.title}>
        {editingBookingId
          ? 'Edit Booking'
          : 'Create Booking'}
      </h3>

      {displayError && (
        <div style={styles.errorBox}>
          {displayError}
        </div>
      )}

      <form onSubmit={handleFormSubmit} className="booking-form">
        {canCreateTrainingBooking && (
          <section className="booking-form__section booking-form__section--mode">
            <div className="booking-form__section-title">Booking mode</div>
            <div style={styles.fieldBlock}>
              <label style={styles.label} htmlFor="booking-mode">Booking mode</label>
              <select
                id="booking-mode"
                name="bookingType"
                aria-label="Booking mode"
                value={form.bookingType || 'normal'}
                onChange={handleChange}
                style={inputStyle}
                disabled={Boolean(editingBookingId)}
              >
                <option value="normal">Normal Flight</option>
                <option value="training">Training Flight</option>
              </select>
            </div>
          </section>
        )}

        {isTraining && canCreateTrainingBooking && (
          <section className="booking-form__section booking-form__section--training">
            <div className="booking-form__section-title">Training</div>
            {trainingOptionsLoading && (
              <p role="status" aria-live="polite">Refreshing student, instructor and enrolment options…</p>
            )}
            <div className="booking-form__grid booking-form__grid--training">
              <div style={styles.fieldBlock}>
                <label style={styles.label} htmlFor="training-student">Student</label>
                <select
                  id="training-student"
                  name="studentUserId"
                  aria-label="Student"
                  value={form.studentUserId || ''}
                  onChange={handleChange}
                  style={inputStyle}
                  disabled={trainingOptionsLoading}
                  required
                >
                  <option value="">Select student</option>
                  {(trainingOptions?.studentProfiles || []).filter((profile) => profile.status === 'active').map((profile) => {
                    const member = usersMap?.[profile.user_id];
                    return <option key={profile.id} value={profile.user_id}>{member?.full_name || member?.email || 'Student'}</option>;
                  })}
                </select>
              </div>
              <div style={styles.fieldBlock}>
                <label style={styles.label} htmlFor="training-instructor">Instructor</label>
                <select
                  id="training-instructor"
                  name="instructorUserId"
                  aria-label="Instructor"
                  value={form.instructorUserId || ''}
                  onChange={handleChange}
                  style={inputStyle}
                  disabled={trainingOptionsLoading}
                  required
                >
                  <option value="">Select instructor</option>
                  {(trainingOptions?.instructorProfiles || []).filter((profile) => profile.status === 'active').map((profile) => {
                    const member = usersMap?.[profile.user_id];
                    return <option key={profile.id} value={profile.user_id}>{member?.full_name || member?.email || 'Instructor'}</option>;
                  })}
                </select>
              </div>
              <div style={styles.fieldBlock}>
                <label style={styles.label} htmlFor="training-course">Course / enrolment</label>
                <select
                  id="training-course"
                  name="courseEnrolmentId"
                  aria-label="Course / enrolment"
                  value={form.courseEnrolmentId || ''}
                  onChange={handleChange}
                  style={inputStyle}
                  disabled={trainingOptionsLoading || !selectedStudentProfile || availableEnrolments.length === 0}
                >
                  <option value="">
                    {trainingOptionsLoading
                      ? 'Refreshing enrolments…'
                      : selectedStudentProfile
                        ? 'No active enrolment'
                        : 'Select a student first'}
                  </option>
                  {availableEnrolments.map((enrolment) => {
                    const course = enrolment.training_courses || {};
                    const courseLabel = [course.code, course.name].filter(Boolean).join(' · ') || 'Course';
                    return <option key={enrolment.id} value={enrolment.id}>{courseLabel}</option>;
                  })}
                </select>
              </div>
              {!form.courseEnrolmentId && (
                <p className="booking-form__training-warning" role="status">
                  No course enrolment is linked. Introductory and trial lessons can proceed, but instructor assessment and digital training-record creation require an active enrolment.
                </p>
              )}
            </div>
          </section>
        )}

        <section className="booking-form__section booking-form__section--aircraft">
          <div className="booking-form__section-title">Aircraft</div>
          <div style={styles.fieldBlock}>
          <label style={styles.label}>
            Aircraft
          </label>

          <select
            name="aircraft"
            aria-label="Aircraft"
            value={form.aircraft}
            onChange={handleChange}
            style={inputStyle}
            required
          >
            <option value="">
              Select aircraft
            </option>

            {aircraftList.map((aircraft) => (
              <option
                key={aircraft.id}
                value={aircraft.id}
              >
                {buildAircraftDisplayLabel(
                  aircraft
                )}
              </option>
            ))}
          </select>

          {selectedAircraft && (
            <div
              style={{
                ...styles.statusPill,
                background:
                  getAircraftServiceabilityMeta(
                    selectedAircraft.status
                  ).background,
                border: `1px solid ${
                  getAircraftServiceabilityMeta(
                    selectedAircraft.status
                  ).border
                }`,
                color:
                  getAircraftServiceabilityMeta(
                    selectedAircraft.status
                  ).text,
              }}
            >
              <span>
                {
                  getAircraftServiceabilityMeta(
                    selectedAircraft.status
                  ).icon
                }
              </span>

              <span>
                {
                  getAircraftServiceabilityMeta(
                    selectedAircraft.status
                  ).label
                }
              </span>
            </div>
          )}
          </div>
        </section>

        <section className="booking-form__section booking-form__section--date">
          <div className="booking-form__section-title">When</div>
          <div style={styles.fieldBlock}>
            <div style={styles.departureDateLabel}>Departure Date</div>
            <div style={styles.departureDateValue}>
              {formatDepartureDate(form.date)}
            </div>
          </div>
        </section>

        <section className="booking-form__section booking-form__section--flight">
          <div className="booking-form__section-title">Flight</div>
          <div style={styles.fieldBlock}>
            <label style={styles.label}>
              Flight Type
            </label>

            <select
              name="flightType"
              aria-label="Flight Type"
              value={form.flightType || 'local'}
              onChange={handleChange}
              style={inputStyle}
            >
              <option value="local">
                Local
              </option>

              <option value="circuits">
                Circuits
              </option>

              <option value="land-away">
                Land Away
              </option>
            </select>
          </div>
        </section>

        {(form.flightType || 'local') === 'land-away' ? (
          <>
            <div className="booking-form__grid booking-form__grid--land-primary">
              <div className="booking-form__departure-time" style={styles.fieldBlock}>
              <label style={styles.label}>
                Departure Time
              </label>

              <input
                name="time"
                aria-label="Departure Time"
                type="time"
                step="60"
                value={form.time}
                onChange={handleStartTimeChange}
                style={inputStyle}
                required
              />
              </div>

              <div className="booking-form__destination-start" style={styles.fieldBlock}>
                <div className="booking-form__section-title">Destination</div>
                <label style={styles.label}>
                  Destination
                </label>

                <input
                  name="destinationAirfield"
                  aria-label="Destination"
                  type="text"
                  value={form.destinationAirfield}
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder="EGXX"
                  required
                />
              </div>
            </div>

            <div className={`booking-form__grid booking-form__grid--land-return${form.showEndDate ? ' booking-form__grid--land-return-four' : ''}`}>
              <div className="booking-form__destination-field" style={styles.fieldBlock}>
                <label style={styles.label}>
                  Return
                </label>

                <select
                  name="landingSameDate"
                  aria-label="Return"
                  value={
                    form.showEndDate
                      ? 'no'
                      : 'yes'
                  }
                  onChange={
                    handleReturnDateChoice
                  }
                  style={inputStyle}
                >
                  <option value="yes">
                    Same day
                  </option>

                  <option value="no">
                    Another day
                  </option>
                </select>
              </div>

              {form.showEndDate && (
                <div className="booking-form__destination-field" style={styles.fieldBlock}>
                  <label style={styles.label}>
                    Arrival Date
                  </label>

                  <input
                    name="endDate"
                    aria-label="Arrival Date"
                    type="date"
                    value={
                      form.endDate ||
                      form.date
                    }
                    min={form.date}
                    onChange={
                      handleEndDateChange
                    }
                    style={inputStyle}
                    required
                  />
                </div>
              )}

              <div className="booking-form__destination-field" style={styles.fieldBlock}>
                <label style={styles.label}>
                  Returning From
                </label>

                <input
                  name="departureAirfield"
                  aria-label="Returning From"
                  type="text"
                  value={
                    form.departureAirfield
                  }
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder="EGXX"
                  required
                />
              </div>

              <div className="booking-form__destination-field" style={styles.fieldBlock}>
                <label style={styles.label}>
                  Arrival Time
                </label>

                <input
                  name="endTime"
                  aria-label="Arrival Time"
                  type="time"
                  step="60"
                  value={form.endTime}
                  onChange={
                    handleEndTimeChange
                  }
                  style={inputStyle}
                  required
                />
              </div>
            </div>
          </>
        ) : (
          <div className="booking-form__grid booking-form__grid--local-times">
            <div style={styles.fieldBlock}>
              <label style={styles.label}>
                Departure Time
              </label>

              <input
                name="time"
                aria-label="Departure Time"
                type="time"
                step="60"
                value={form.time}
                onChange={handleStartTimeChange}
                style={inputStyle}
                required
              />
            </div>

            <div style={styles.fieldBlock}>
              <label style={styles.label}>
                Return Time
              </label>

              <input
                name="endTime"
                aria-label="Return Time"
                type="time"
                step="60"
                value={form.endTime}
                onChange={handleEndTimeChange}
                style={inputStyle}
                required
              />
            </div>
          </div>
        )}

        <section className="booking-form__section booking-form__section--extras">
          <div className="booking-form__section-title">Extras</div>
          <div style={styles.fieldBlock}>
            <label style={styles.label}>
              Notes
            </label>
            <textarea
              name="notes"
              aria-label="Notes"
              value={form.notes}
              onChange={handleChange}
              style={inputStyle}
              rows="3"
            />
          </div>
        </section>

        <div className="booking-form__actions">
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              ...buttonStyle,
              ...styles.submitButton,
              opacity: isSubmitting ? 0.65 : 1,
              cursor: isSubmitting ? 'wait' : 'pointer',
            }}
          >
            {isSubmitting
              ? editingBookingId
                ? 'Saving...'
                : 'Creating...'
              : editingBookingId
                ? 'Save Booking'
                : 'Create Booking'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

const styles = {
  kicker: {
    color: '#55c7ff',
    fontSize: '0.72rem',
    fontWeight: 900,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    marginBottom: '0.35rem',
  },

  title: {
    color: '#f3f8ff',
    margin: '0 0 1.15rem',
    fontSize: '1.35rem',
    fontWeight: 850,
    letterSpacing: '-0.03em',
  },

  errorBox: {
    background: '#3a1111',
    color: '#ffb4b4',
    border: '1px solid #7d2c2c',
    borderRadius: 10,
    padding: '0.7rem 0.9rem',
    marginBottom: '0.9rem',
    fontSize: '0.9rem',
    lineHeight: 1.4,
  },

  grid: {
    display: 'grid',
    gridTemplateColumns:
      'repeat(2, minmax(0, 1fr))',
    gap: '0.8rem',
  },

  fieldBlock: {
    marginBottom: '0.9rem',
  },

  departureDateLabel: {
    color: '#d7ebff',
    fontSize: '0.78rem',
    fontWeight: 900,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginBottom: '0.2rem',
  },

  departureDateValue: {
    color: '#f3f8ff',
    fontSize: '0.95rem',
    fontWeight: 700,
  },

  label: {
    display: 'block',
    color: '#d7ebff',
    fontSize: '0.78rem',
    fontWeight: 900,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginBottom: '0.35rem',
  },

  statusPill: {
    marginTop: '0.55rem',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.38rem 0.7rem',
    borderRadius: 999,
    fontSize: '0.78rem',
    fontWeight: 900,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },

  submitButton: {
    width: '100%',
    marginTop: '0.2rem',
    fontSize: '0.9rem',
    fontWeight: 900,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
};